"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { I18nData, LocaleData } from '@/types/i18n';

interface LocaleContextType {
  locale: string;
  setLocale: (locale: string) => void;
  availableLocales: string[];
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const getBrowserLocale = (availableLocales: string[]): string => {
  const browserLang = navigator.language.split('-')[0].toLowerCase();
  return availableLocales.includes(browserLang) ? browserLang : 'en';
};

const getUrlLocale = (): string | null => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('locale');
};

const setUrlLocale = (locale: string): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('locale', locale);
  window.history.replaceState({}, '', url.toString());
};

export function LocaleProvider({ 
  children, 
  availableLocales,
  defaultLocale = 'en' 
}: { 
  children: ReactNode;
  availableLocales: string[];
  defaultLocale?: string;
}) {
  const [locale, setLocaleState] = useState<string>(defaultLocale);

  useEffect(() => {
    if (availableLocales.length <= 1) return;

    const urlLocale = getUrlLocale();
    const storedLocale = localStorage.getItem('preferred-locale');
    
    const selectedLocale = 
      (urlLocale && availableLocales.includes(urlLocale)) ? urlLocale :
      (storedLocale && availableLocales.includes(storedLocale)) ? storedLocale :
      getBrowserLocale(availableLocales);

    setLocaleState(selectedLocale);
    localStorage.setItem('preferred-locale', selectedLocale);
  }, [availableLocales]);

  const setLocale = useCallback((newLocale: string) => {
    if (!availableLocales.includes(newLocale)) return;

    setLocaleState(newLocale);
    localStorage.setItem('preferred-locale', newLocale);
    setUrlLocale(newLocale);
  }, [availableLocales]);

  const contextValue = useMemo(() => ({
    locale,
    setLocale,
    availableLocales
  }), [locale, setLocale, availableLocales]);

  return (
    <LocaleContext.Provider value={contextValue}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}

export function useLocalizedData(i18nData?: I18nData): LocaleData {
  const { locale } = useLocale();
  
  return useMemo(() => {
    return i18nData?.[locale] || i18nData?.en || {};
  }, [i18nData, locale]);
}